import React, { useState, useRef, useEffect } from "react";
import { Card, CardContent } from "../ui/card";
import {
  Upload,
  FileText,
  Camera,
  Check,
  X,
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Info,
  Car,
} from "lucide-react";
import ExtendedWarrantyScreenWrapper, {
  ExtendedWarrantyHeader,
  ExtendedWarrantyCard,
  ExtendedWarrantyButton,
} from "./ExtendedWarrantyScreenWrapper";
import { useAuth } from "../../hooks/useAuth";
import insurancePolicyService from "../../lib/InsurancePolicyService";
import analyticsService from "../../lib/AnalyticsService";
import { useExtractionStream } from "../../hooks/useExtractionStream";

// Upload stages configuration
const UPLOAD_STAGES = [
  { id: "setting-up", label: "Setting up", duration: 2000, image: "/upload/SETTING-UP.gif" },
  { id: "uploading", label: "Uploading document", duration: 6000, image: "/upload/UPLOADING.gif" },
  { id: "parsing", label: "Parsing document", duration: 3500, image: "/upload/PARSING.gif" },
  { id: "extracting", label: "Extracting data with AI", duration: 4000, image: "/upload/EXTRACTING.gif" },
  { id: "organizing", label: "Organizing your info", duration: 1500, image: "/upload/ORGANIZING.gif" },
];

// SSE stream endpoints per flow (TBD — update when backend endpoints are ready)
const STREAM_ENDPOINTS = {
  ExtendedWarranty: "/api/insurance/extract-stream",
  Refinancing: "/api/insurance/extract-stream",
};

const ExtendedWarrantyPolicyUploadScreen = ({ flow, onComplete, onBack }) => {
  const { user, getIdToken } = useAuth();
  const [existingPolicy, setExistingPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const uploadResultRef = useRef(null);

  const flowType = flow === "Refinancing" ? "refinancing" : "extended-warranty";

  // Upload stage state (null = not uploading, or stage id)
  const [uploadStage, setUploadStage] = useState(null);

  // SSE extraction stream hook
  const {
    startStream,
    abort: abortStream,
    stage: sseStage,
    message: sseMessage,
    extractedData,
    completed: sseCompleted,
    error: sseError,
  } = useExtractionStream();

  // Elapsed time counter — ticks every second while overlay is visible
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  useEffect(() => {
    if (uploadStage) {
      setElapsedSeconds(0);
      const interval = setInterval(() => {
        setElapsedSeconds((prev) => prev + 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [!!uploadStage]); // only reset when uploadStage goes null <-> non-null

  // Drive uploadStage from SSE events
  useEffect(() => {
    if (sseStage) {
      setUploadStage(sseStage);
    }
  }, [sseStage]);

  // Summary card state
  const [showSummary, setShowSummary] = useState(false);
  const [summaryCountdown, setSummaryCountdown] = useState(5);

  // Handle SSE completion — show summary on success, skip straight to next step on error
  useEffect(() => {
    if (sseCompleted && uploadResultRef.current) {
      // Save extracted data to sessionStorage for downstream screens
      if (extractedData.quick || extractedData.full) {
        sessionStorage.setItem(
          "extractedData",
          JSON.stringify(extractedData)
        );
      }

      if (sseError) {
        // Extraction failed — skip summary, advance silently (non-blocking)
        handleSummaryAdvance();
      } else {
        setShowSummary(true);
      }
    }
  }, [sseCompleted, extractedData, sseError]);

  // Auto-advance countdown when summary is shown
  useEffect(() => {
    if (!showSummary) return;
    setSummaryCountdown(5);
    const interval = setInterval(() => {
      setSummaryCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          handleSummaryAdvance();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [showSummary]);

  const handleSummaryAdvance = () => {
    setShowSummary(false);
    setUploadStage(null);
    setUploadSuccess(true);
    setTimeout(() => {
      if (onComplete) {
        onComplete(uploadResultRef.current);
      }
    }, 500);
  };

  useEffect(() => {
    analyticsService.trackPageView("ew_policy_upload", {
      screen: "policy_upload",
      flow_type: flowType,
    });
  }, [flowType]);

  useEffect(() => {
    // Check if user has existing policies
    const fetchPolicies = async () => {
      try {
        if (!user || !user.uid) {
          setError("User authentication required");
          setLoading(false);
          return;
        }

        setLoading(true);
        setError(null);
        const token = await getIdToken();

        const result = await insurancePolicyService.checkExistingPolicies(
          token
        );

        if (result) {
          setExistingPolicy(result.policy);
          analyticsService.trackEvent("Extended Warranty Existing Policy Found", {
            has_existing_policy: true,
            screen: "policy_upload",
            flow_type: flowType,
          });
        }
        setLoading(false);
      } catch (err) {
        console.error("Error checking existing policies:", err);
        analyticsService.trackError("ew_policy_check_error", err, { screen: "policy_upload", flow_type: flowType });
        setError("Unable to check for existing policies. Please try again.");
        setLoading(false);
      }
    };
    fetchPolicies();
  }, [user]);

  // Detect if the user is on a mobile device
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
      setIsMobile(mobileRegex.test(userAgent));
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const validateFile = (file) => {
    const MAX_SIZE = 10 * 1024 * 1024; // 10MB
    if (!file.type.startsWith("image/") && file.type !== "application/pdf") {
      throw new Error("Please upload an image or PDF file");
    }
    if (file.size > MAX_SIZE) {
      throw new Error("File size must be less than 10MB");
    }
  };

  const handleFileSelect = (file) => {
    try {
      validateFile(file);
      setSelectedFile(file);

      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setPreview(null);
      }

      setError(null);
      setUploadSuccess(false);
    } catch (err) {
      setError(err.message);
      setSelectedFile(null);
      setPreview(null);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files?.[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleCameraCapture = () => {
    if (isMobile && cameraInputRef.current) {
      cameraInputRef.current.click();
    } else {
      setError(
        "Camera works best on mobile devices. You may continue if your device has a camera, or use the file upload option instead."
      );
      setTimeout(() => {
        if (cameraInputRef.current) {
          cameraInputRef.current.click();
        }
      }, 1500);
    }
  };

  const handleUpload = async () => {
    try {
      if (!selectedFile) {
        throw new Error("Please select a file first");
      }

      setUploadStage("setting-up");
      setError(null);
      uploadResultRef.current = null;

      analyticsService.trackEvent("Extended Warranty Upload Initiated", {
        file_type: selectedFile.type,
        file_size_bytes: selectedFile.size,
        screen: "policy_upload",
        flow_type: flowType,
      });

      const token = await getIdToken();

      // Step 1: Upload file
      setUploadStage("uploading");
      const fileUrl = await insurancePolicyService.uploadFile(
        selectedFile,
        token
      );

      // Cache file URL
      const fileUrlData = {
        url: fileUrl,
        fileName: selectedFile.name,
        fileType: selectedFile.type,
        uploadedAt: new Date().toISOString(),
      };
      sessionStorage.setItem(
        "cachedInsuranceFileUrl",
        JSON.stringify(fileUrlData)
      );

      // Step 2: Submit checkin (flow-specific endpoint)
      let checkinResult;
      if (flow === "ExtendedWarranty") {
        checkinResult = await insurancePolicyService.submitPolicyCheckinForExtendedWarranty(
          fileUrl,
          token
        );
      } else {
        checkinResult = await insurancePolicyService.submitPolicyCheckinForRefinancing(
          fileUrl,
          token
        );
      }

      const result = {
        success: true,
        result: checkinResult,
        fileUrl,
        type: "insurance_declaration_upload",
        timestamp: new Date().toISOString(),
        fileName: selectedFile.name,
        policyCheckInId: checkinResult?.id || null,
      };
      uploadResultRef.current = result;

      // Store submission info in session storage
      sessionStorage.setItem(
        "documentData",
        JSON.stringify({
          documentType: "DEC_Auto",
          timestamp: new Date().toISOString(),
          fileName: selectedFile.name,
          policyCheckInId: checkinResult?.id || null,
        })
      );

      analyticsService.trackFileUpload(selectedFile.name, selectedFile.size, selectedFile.type, true, null);

      // Step 3: Start SSE extraction stream (drives stages from here)
      const streamEndpoint = STREAM_ENDPOINTS[flow] || null;
      setUploadStage("parsing");
      startStream(checkinResult.id, token, streamEndpoint);

    } catch (err) {
      console.error("Upload error:", err);
      abortStream();
      analyticsService.trackFileUpload(selectedFile?.name, selectedFile?.size, selectedFile?.type, false, err);
      setError(err.message || "An unexpected error occurred");
      setUploadStage(null);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    setPreview(null);
    setError(null);
    setUploadSuccess(false);
  };

  return (
    <ExtendedWarrantyScreenWrapper>
      {/* Translucent loading overlay — progress view OR summary view */}
      {(uploadStage || showSummary) && (() => {
        const currentStage = UPLOAD_STAGES.find((s) => s.id === uploadStage);
        const currentIndex = UPLOAD_STAGES.findIndex((s) => s.id === uploadStage);
        const quickData = extractedData?.quick;
        const fullData = extractedData?.full;
        const summaryData = fullData || quickData;
        const vehicles = fullData?.vehicles || [];
        const totalPremium = fullData?.total_premium_cents
          ? (parseInt(fullData.total_premium_cents, 10) / 100).toFixed(2)
          : null;
        const coverageCount = vehicles.reduce(
          (sum, v) => sum + (v.coverages?.length || 0), 0
        );

        return (
          <div className="fixed inset-0 flex flex-col items-center z-50 overflow-y-auto pt-12 pb-8">
            {/* GIF background */}
            {!showSummary && currentStage?.image && (
              <img
                src={currentStage.image}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
              />
            )}
            {/* Dark overlay on top of GIF */}
            <div className="absolute inset-0 bg-black/25"></div>
            {/* Content on top */}
            <div className="relative z-10 flex flex-col items-center w-full">
            {showSummary ? (
              <>
                {/* Summary card */}
                <div className="w-[90%] max-w-md bg-white rounded-xl px-5 py-5 shadow-xl space-y-4">
                  {/* Header */}
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-[#A1CE90]" />
                    <p className="text-sm font-bold text-gray-800 uppercase tracking-wide">
                      Extraction Complete
                    </p>
                  </div>

                  {/* Policy basics */}
                  <div className="space-y-1">
                    {summaryData?.carrier_name && (
                      <p className="text-base font-semibold text-gray-900">
                        {summaryData.carrier_name}
                      </p>
                    )}
                    {summaryData?.carrier_policy_number && (
                      <p className="text-sm text-gray-600">
                        Policy #{summaryData.carrier_policy_number}
                      </p>
                    )}
                    {(summaryData?.effective_date || summaryData?.expiry_date) && (
                      <p className="text-sm text-gray-500">
                        {summaryData.effective_date}
                        {summaryData.expiry_date && ` \u2192 ${summaryData.expiry_date}`}
                      </p>
                    )}
                    {summaryData?.insured_name && (
                      <p className="text-sm text-gray-600">
                        {summaryData.insured_name}
                      </p>
                    )}
                  </div>

                  {/* Vehicle info */}
                  {vehicles.length > 0 && (
                    <div className="border-t pt-3 space-y-2">
                      {vehicles.map((vehicle, i) => (
                        <div key={i}>
                          <p className="text-sm font-semibold text-gray-800">
                            {[vehicle.year, vehicle.make, vehicle.model]
                              .filter(Boolean)
                              .join(" ")}
                          </p>
                          {vehicle.vin && (
                            <p className="text-xs text-gray-500">
                              VIN: {vehicle.vin}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Coverage summary */}
                  {(coverageCount > 0 || totalPremium) && (
                    <div className="border-t pt-3 flex items-center justify-between">
                      {coverageCount > 0 && (
                        <p className="text-sm text-gray-600">
                          {coverageCount} coverage{coverageCount !== 1 ? "s" : ""} found
                        </p>
                      )}
                      {totalPremium && (
                        <p className="text-sm font-semibold text-gray-800">
                          Total Premium: ${totalPremium}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Continue button + countdown */}
                  <div className="flex items-center justify-between pt-2">
                    <button
                      onClick={handleSummaryAdvance}
                      className="uppercase font-bold bg-purpleGradient text-white px-6 py-2.5 rounded-[10px] hover:bg-purpleGradientDark transition-colors text-sm"
                    >
                      Continue
                    </button>
                    <p className="text-xs text-gray-400">
                      Continuing in {summaryCountdown}s...
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Progress view — status + quick extract + dots */}
                {/* Status card with spinner + elapsed time */}
                <div className="w-[90%] max-w-md mb-3 bg-white/90 backdrop-blur-md rounded-xl px-5 py-4 shadow-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 border-2 border-[#7B61FF] border-t-transparent rounded-full animate-spin flex-shrink-0"></div>
                    <p className="text-sm font-semibold text-gray-700">
                      {sseMessage || currentStage?.label}
                    </p>
                  </div>
                  <p className="text-xs text-gray-400 mt-1 ml-8">
                    {elapsedSeconds}s elapsed
                  </p>
                </div>

                {/* Extracted data card — fades in after quick_extract */}
                <div
                  className={`w-[90%] max-w-md mb-4 transition-all duration-500 ${
                    quickData
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 -translate-y-2 h-0 mb-0 overflow-hidden"
                  }`}
                >
                  {quickData && (
                    <div className="bg-white/90 backdrop-blur-md rounded-xl px-5 py-4 shadow-lg space-y-2">
                      <p className="text-xs font-semibold text-[#7B61FF] uppercase tracking-wide mb-2">
                        Policy Found
                      </p>
                      {quickData.insured_name && (
                        <div className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-[#A1CE90] flex-shrink-0" />
                          <span className="text-sm text-gray-700">{quickData.insured_name}</span>
                        </div>
                      )}
                      {quickData.carrier_name && (
                        <div className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-[#A1CE90] flex-shrink-0" />
                          <span className="text-sm text-gray-700">{quickData.carrier_name}</span>
                        </div>
                      )}
                      {quickData.carrier_policy_number && (
                        <div className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-[#A1CE90] flex-shrink-0" />
                          <span className="text-sm text-gray-700">Policy #{quickData.carrier_policy_number}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Progress dots */}
                <div className="flex items-center gap-2 mt-4">
                  {UPLOAD_STAGES.map((stage, index) => (
                    <div
                      key={stage.id}
                      className={`h-2 rounded-full transition-all duration-300 ${
                        index <= currentIndex
                          ? "bg-[#7B61FF] w-6"
                          : "bg-gray-300 w-2"
                      }`}
                    />
                  ))}
                </div>

                <p className="text-sm text-white/80 mt-3">
                  Please don't close this window
                </p>
              </>
            )}
            </div>
          </div>
        );
      })()}

      {/* Back button */}
      {onBack && (
        <button
          onClick={onBack}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </button>
      )}

      {loading ? (
        <div className="space-y-6">
          <Card>
            <CardContent className="pt-6 flex flex-col items-center justify-center min-h-40">
              <div className="w-10 h-10 border-4 border-t-transparent border-blue-600 rounded-full animate-spin mb-4"></div>
              <p className="text-gray-600">Checking for existing policies...</p>
            </CardContent>
          </Card>
        </div>
      ) : existingPolicy ? (
        // Show existing policy found
        <>
          <ExtendedWarrantyCard variant="success">
            <div className="flex items-start gap-3">
              <Check className="w-5 h-5 text-green-500 mt-1" />
              <div>
                <p className="font-medium text-green-900">
                  Policy Document Already Uploaded
                </p>
                <p className="text-sm text-green-700 mt-1">
                  {existingPolicy.provider &&
                    `Provider: ${existingPolicy.provider}`}
                  {existingPolicy.policyNumber &&
                    `, Policy #: ${existingPolicy.policyNumber
                      .slice(-4)
                      .padStart(existingPolicy.policyNumber.length, "*")}`}
                </p>
                <p className="text-xs text-green-700 mt-1">
                  Uploaded on{" "}
                  {new Date(existingPolicy.checkedInAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          </ExtendedWarrantyCard>

          <ExtendedWarrantyButton
            onClick={() => onComplete && onComplete(existingPolicy)}
            variant="primary"
          >
            <ArrowRight className="w-4 h-4" />
            <span>Continue</span>
          </ExtendedWarrantyButton>
        </>
      ) : (
        <>
          {/* Header */}
          <ExtendedWarrantyHeader
            title="Upload your Proof of Insurance"
            subtitle="This allows us to pre-fill and verify your vehicle info automatically."
          />

          {/* What we'll extract info card */}
          <ExtendedWarrantyCard variant="info">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">
                  What is a Proof of Insurance Document:
                </h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>You can upload your insurance declaration form</li>
                  <li>You can upload your insurance card</li>
                </ul>
              </div>
            </div>
          </ExtendedWarrantyCard>

          {/* Upload Area */}
          <ExtendedWarrantyCard>
            {!selectedFile ? (
              <div className="space-y-4">
                {/* Hidden file inputs */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileInput}
                  accept="image/*,application/pdf"
                  className="hidden"
                />
                <input
                  type="file"
                  ref={cameraInputRef}
                  onChange={handleFileInput}
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                />

                {/* Upload options */}
                <div
                  className={
                    isMobile
                      ? "grid grid-cols-2 gap-4"
                      : "flex justify-center gap-4"
                  }
                >
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="flex flex-col items-center gap-3 p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                  >
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <Upload className="w-6 h-6 text-blue-500" />
                    </div>
                    <div className="text-center">
                      <div className="font-medium text-gray-900">
                        Choose File
                      </div>
                      <div className="text-sm text-gray-600">PDF or Image</div>
                    </div>
                  </button>

                  {isMobile && (
                    <button
                      onClick={handleCameraCapture}
                      className="flex flex-col items-center gap-3 p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                    >
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                        <Camera className="w-6 h-6 text-green-500" />
                      </div>
                      <div className="text-center">
                        <div className="font-medium text-gray-900">
                          Take Photo
                        </div>
                        <div className="text-sm text-gray-600">Use Camera</div>
                      </div>
                    </button>
                  )}
                </div>

                <p className="text-sm text-gray-500 text-center">
                  {isMobile
                    ? "You can take a photo or upload from your device"
                    : "Upload a PDF or image document (max 10MB)"}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* File preview */}
                <div className="relative">
                  {preview ? (
                    <img
                      src={preview}
                      alt="Document preview"
                      className="w-full max-h-64 object-contain rounded-lg border"
                    />
                  ) : (
                    <div className="p-6 border rounded-lg bg-gray-50 flex flex-col items-center justify-center">
                      <FileText className="w-16 h-16 text-blue-500 mb-3" />
                      <span className="font-medium text-gray-900 text-center break-all">
                        {selectedFile.name}
                      </span>
                      <span className="text-sm text-gray-500 mt-1">
                        PDF Document (
                        {(selectedFile.size / 1024 / 1024).toFixed(1)} MB)
                      </span>
                    </div>
                  )}

                  <button
                    onClick={removeFile}
                    className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-lg hover:bg-gray-100"
                  >
                    <X className="w-5 h-5 text-gray-600" />
                  </button>
                </div>

                {/* Success message */}
                {uploadSuccess && (
                  <div className="flex items-start gap-2 p-3 bg-green-50 text-green-800 rounded-lg text-sm">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <p>
                      Declaration page uploaded and processed successfully!
                    </p>
                  </div>
                )}

                {/* File info */}
                {!uploadSuccess && (
                  <div className="flex items-center gap-2 text-green-600">
                    <Check className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      {selectedFile.name} ready for upload
                    </span>
                  </div>
                )}
              </div>
            )}
          </ExtendedWarrantyCard>

          {/* Error display */}
          {error && (
            <ExtendedWarrantyCard variant="error">
              <div className="flex items-start gap-2 text-red-600">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            </ExtendedWarrantyCard>
          )}

          {/* Upload button */}
          {selectedFile && !uploadSuccess && (
            <ExtendedWarrantyButton
              onClick={handleUpload}
              disabled={uploadStage !== null}
            >
              {uploadStage ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  <span>Upload</span>
                </>
              )}
            </ExtendedWarrantyButton>
          )}
        </>
      )}
    </ExtendedWarrantyScreenWrapper>
  );
};

export default ExtendedWarrantyPolicyUploadScreen;
